/**
 * Transcription API - Ses dosyasını konuşmacı ayrımlı metne çevirir;
 * özet, çeviri, sohbet ve TXT/Word/SRT/VTT dışa aktarma.
 */
import { apiRequest, API_BASE_URL, tokenManager, refreshAccessToken } from './api';

const BASE = '/transcription';

async function fetchAudioArrayBuffer(id) {
    const url = `${API_BASE_URL}${BASE}/${id}/audio/`;
    const authHeaders = () => {
        const token = tokenManager.getAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    let response = await fetch(url, { headers: authHeaders() });
    if (response.status === 401 && tokenManager.getAccessToken()) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            response = await fetch(url, { headers: authHeaders() });
        }
    }
    if (!response.ok) {
        throw new Error(`Ses yüklenemedi (HTTP ${response.status}).`);
    }
    return response.arrayBuffer();
}

export const transcriptionAPI = {
    list: async () => {
        const res = await apiRequest(`${BASE}/`);
        // DRF sayfalama: {results:[...]} ya da düz dizi
        return Array.isArray(res) ? res : (res?.results || []);
    },

    get: async (id) => apiRequest(`${BASE}/${id}/`),

    upload: async (file, { title, language, numSpeakers, speakerRefs } = {}) => {
        const formData = new FormData();
        formData.append('file', file);
        if (title) formData.append('title', title);
        if (language) formData.append('language', language);
        if (numSpeakers != null && numSpeakers !== '') {
            formData.append('num_speakers', String(numSpeakers));
        }
        (speakerRefs || []).slice(0, 3).forEach((ref, idx) => {
            if (ref?.name && ref?.file) {
                formData.append(`speaker_ref_name_${idx}`, ref.name);
                formData.append(`speaker_ref_file_${idx}`, ref.file);
            }
        });
        return apiRequest(`${BASE}/upload/`, 'POST', formData);
    },

    update: async (id, data) => apiRequest(`${BASE}/${id}/`, 'PATCH', data),

    remove: async (id) => apiRequest(`${BASE}/${id}/`, 'DELETE'),

    summarize: async (id) => apiRequest(`${BASE}/${id}/summarize/`, 'POST', {}),

    translate: async (id, targetLanguage) =>
        apiRequest(`${BASE}/${id}/translate/`, 'POST', { target_language: targetLanguage }),

    chat: async (id, message, history = []) =>
        apiRequest(`${BASE}/${id}/chat/`, 'POST', { message, history }),

    // Erişim yönetimi (admin) — bu modülü görebilecek kullanıcılar
    getModuleAccessUsers: async () => apiRequest(`${BASE}/module-access/`),
    updateModuleAccessUsers: async (userIds) =>
        apiRequest(`${BASE}/module-access/`, 'PUT', { user_ids: userIds }),

    fetchAudioArrayBuffer,

    downloadAudio: async (id, filename) => {
        const buf = await fetchAudioArrayBuffer(id);
        const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'kayit.wav';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    },

    // Export dosyasını JWT ile indirir ve tarayıcıda kaydetmeyi tetikler
    downloadExport: async (id, format, filename) => {
        const response = await fetch(`${API_BASE_URL}${BASE}/${id}/export/?format=${format}`, {
            headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
        });
        if (!response.ok) {
            throw new Error(`Dışa aktarma başarısız oldu (HTTP ${response.status}).`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `transkript.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    },
};
